<template>
  <b-card title="Upload Data to API" class="p-4 mx-auto mt-4" style="width: 50%;">
    <div>
      <!-- Date Picker -->
      <b-form-group label="Select Date">
        <b-form-datepicker v-model="selectedDate" placeholder="Select date"></b-form-datepicker>
      </b-form-group>

      <input type="file" @change="handleFileUpload" /> <br><br>

      <b-row class="mb-3">
        <b-col cols="auto" class="d-flex justify-content-center mb-2">
          <b-button @click="uploadData('embarkation')" variant="primary">Embark</b-button>
        </b-col>
        <b-col cols="auto" class="d-flex justify-content-center mb-2">
          <b-button @click="uploadData('debarkation')" variant="primary">Debark</b-button>
        </b-col>
      </b-row>

      <b-progress v-if="showProgress" :value="uploadProgress" max="100" class="mt-4">
        <b-progress-bar :value="uploadProgress">{{ uploadProgress }}%</b-progress-bar>
      </b-progress>
    </div>

    <b-toast v-model="toastVisible" title="Upload Status" :variant="toastVariant" solid>
      {{ toastMessage }}
    </b-toast>
  </b-card>
</template>

<script>
import * as XLSX from 'xlsx';
import moment from 'moment';

export default {
  data() {
    return {
      excelData: [],
      uploadProgress: 0,
      toastVisible: false,
      showProgress: false,
      selectedDate: null,
      toastMessage: '',
      toastVariant: 'success',
    };
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        this.excelData = jsonData;
      };

      reader.readAsArrayBuffer(file);
    },
    async uploadData(endpoint) {
      if (!this.selectedDate) {
        alert('Please select a date.');
        return;
      }

      if (this.excelData.length === 0) {
        alert('No data to upload.');
        return;
      }

      this.showProgress = true;
      this.uploadProgress = 0;

      const formattedDate = moment(this.selectedDate).format('YYYY-MM-DD');

      let errorOccurred = false;
      let errorMessage = '';

      const normalize = (val) => (val || '').toString().trim().toLowerCase();
      const createKey = (row) =>
        `${normalize(row.bookingNumber)}|${normalize(row.guestsLastName)}|${normalize(row.firstName)}|${normalize(row.flightNumber)}`;

      try {
        // 1. Find or Create the ShipCall
        let shipCallId = null;
        const callQueryUrl = `/api/ship-calls?filters[operationDate][$eq]=${formattedDate}&filters[operationType][$eq]=${endpoint}`;
        const callResponse = await this.$xhr.get(callQueryUrl);
        
        if (callResponse.data?.data && callResponse.data.data.length > 0) {
          shipCallId = callResponse.data.data[0].id;
          console.log(`Found existing ShipCall with ID: ${shipCallId}`);
        } else {
          console.log(`Creating new ShipCall for ${endpoint} on ${formattedDate}`);
          const newCallResponse = await this.$xhr.post('/api/ship-calls', {
            data: {
              operationDate: formattedDate,
              operationType: endpoint,
              status: 'active'
            }
          });
          shipCallId = newCallResponse.data.data.id;
        }

        // 2. Fetch existing transfers for this ShipCall to prevent duplicates
        const transfersUrl = `/api/transfers?filters[shipCall][id][$eq]=${shipCallId}&pagination[pageSize]=10000`;
        const existingResponse = await this.$xhr.get(transfersUrl);
        
        const apiRows = existingResponse?.data?.data || [];
        const existingData = apiRows.map(row => row.attributes || {});

        const existingKeys = new Set(existingData.map(createKey));

        // 3. Filter new rows
        const newRows = this.excelData.filter(row => !existingKeys.has(createKey(row)));

        const totalRows = newRows.length;
        if (totalRows === 0) {
          this.toastMessage = 'No new data to upload. All entries already exist.';
          this.toastVariant = 'info';
          this.toastVisible = true;
          this.showProgress = false;
          return;
        }

        // 4. Upload new rows, linking them to the ShipCall
        for (const [index, row] of newRows.entries()) {
          try {
            await this.$xhr.post('/api/transfers', {
              data: { ...row, shipCall: shipCallId }
            });
          } catch (error) {
            console.error('Error uploading data:', error);
            errorOccurred = true;
            errorMessage = error.message || 'Unknown error during upload.';
          }

          this.uploadProgress = Math.round(((index + 1) / totalRows) * 100);
        }

        this.toastVisible = true;
        this.toastMessage = errorOccurred
          ? `Upload completed with some errors: ${errorMessage}`
          : 'Data uploaded successfully!';
        this.toastVariant = errorOccurred ? 'danger' : 'success';
      } catch (err) {
        console.error('Error during upload process:', err);
        this.toastVisible = true;
        this.toastMessage = 'An error occurred during the upload process.';
        this.toastVariant = 'danger';
      }

      this.showProgress = false;
    }

  },
};
</script>

<style scoped>
.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
</style>